import { loadMoreOptionValue } from './Select.constants';
import { LoadMoreComponent, getLoadMoreOption } from './Select.helpers';
import {
  filterInputValue,
  getIsLoadingOption,
  getMultiDummyOption,
} from './Select.helpers';
import { InternalOption, CreatableSelectInstance } from './Select.types';
import { Option, Options, OptionOrGroup } from '../../types';
import { logComponentOrElementMemoizationWarning } from '../../utils/isComponentOrElementMemoized';

// Track which optionRenderer functions we've already checked

export const createProxies = (component: any) => ({
  optionRendererProxy: (option: OptionOrGroup) => {
    const { optionRenderer, optionRendererIgnoresSpecialOptions = false } =
      component.props;
    if (
      optionRenderer == null ||
      ((option as InternalOption).isSpecial &&
        optionRendererIgnoresSpecialOptions)
    ) {
      return component.defaultOptionRenderer(option);
    }

    const result = optionRenderer(option);

    if (optionRenderer) {
      logComponentOrElementMemoizationWarning({
        componentOrElement: result,
        objectToMarkAsCheckedForMemoization: optionRenderer,
        errorMessage:
          'Select: optionRenderer should be memoized with useCallback and return a memoized component (wrapped in React.memo()) to prevent unnecessary re-renders. If this warning appears repeatedly, the optionRenderer function itself is not memoized.',
        errorKey: 'Select: optionRenderer not memoized',
      });
    }

    return result;
  },

  filterOptionsProxy: (
    options: Options,
    input: string,
    excludeOptions: any, // TODO(TS): fix this `any`?
    ...rest: any[] // TODO(TS): fix this `any`?
  ) => {
    const {
      anchorType = 'auto',
      clearable,
      filterOptions,
      ignoreAccents,
      ignoreCase,
      isLoading: isLoadingProp,
      loadMoreComponent = LoadMoreComponent,
      multi,
      resetValue = '',
      value,
    } = component.props;
    const { isLoading, paginationCache, resetOption } = component.state;

    let filterOptionsFunc;
    if (filterOptions === false) {
      filterOptionsFunc = (x: unknown) => x;
    } else if (typeof filterOptions === 'function') {
      filterOptionsFunc = filterOptions;
    } else {
      filterOptionsFunc = component.getDefaultFilterOptionsFunc();
    }

    const showSelectedOptions = component.areOptionsToggleable({
      anchorType,
      multi,
    });
    let filteredOptions: Options = filterOptionsFunc(
      options,
      input,
      showSelectedOptions ? [] : excludeOptions,
      ...rest
    );

    if (input.length === 0) {
      if (multi && options.length > 0 && filteredOptions.length === 0) {
        filteredOptions = filteredOptions.concat(getMultiDummyOption());
      }

      if (clearable && !multi && value != null && value !== resetValue) {
        filteredOptions = (resetOption != null ? [resetOption] : []).concat(
          filteredOptions
        );
      }
    }

    if (isLoading || isLoadingProp) {
      // Add "Loading..." option, if needed
      filteredOptions = filteredOptions.concat(getIsLoadingOption());
    } else {
      const paginationCacheKey = filterInputValue(input, {
        ignoreAccents,
        ignoreCase,
      });
      const pagination = paginationCache![paginationCacheKey];
      // Add "Load more" option, if needed (#2031)
      if (pagination && pagination.hasMore) {
        filteredOptions = filteredOptions.concat(
          getLoadMoreOption({
            input,
            loadMoreComponent,
            options,
            pagination,
          })
        );
      }
    }

    return filteredOptions;
  },

  selectValueProxy: (
    selectValue: (option: Option) => void,
    selectedOption: Option
  ) => {
    // If this is a "Load more" option, trigger load.
    if (selectedOption.value === loadMoreOptionValue) {
      component.triggerLoadMore();
    }
    // Prevent "special" options from being selected.
    if ((selectedOption as InternalOption).isSpecial) {
      return;
    }
    selectValue(selectedOption);
  },

  creatableOnInputChangeProxy: (str: string) => {
    const internalCreatableSelect = component.getInternalCreatableSelect();
    const newInputValue = component.handleInputChange(str);
    if (internalCreatableSelect) {
      (
        internalCreatableSelect as unknown as CreatableSelectInstance
      ).inputValue = newInputValue;
    }
    return newInputValue;
  },

  loadOptionsProxy: (input: string, loadOptionsCallback: Function) => {
    const { open, loadOptions } = component.props;

    if (input == null) {
      return;
    }

    // Show loading indicator until loadOptionsProxyCallback is called
    component.setState({
      isLoading: true,
    });
    component._latestSearch = open ? input : null; // Special case: autoload search is designated as null

    // Keep a copy of all options returned by loadOptions
    const { paginationCache } = component.state;
    const proxyCallback = component.getLoadOptionsProxyCallback(
      open ? input : null,
      loadOptionsCallback
    );
    let result;
    try {
      result = loadOptions!(
        input,
        proxyCallback,
        paginationCache![input || '']
      );
    } catch (error) {
      proxyCallback(error);
      setTimeout(() => {
        throw error;
      }, 0);
    }
    if (result && typeof result.then === 'function') {
      return result.then(
        (data: any) => {
          proxyCallback(null, data || {});
        },
        (error: unknown) => {
          proxyCallback(error);
          throw error;
        }
      );
    }
  },
});
